import { ProdutoPromocional } from '../models/produtos/produto-promocional';

/**
 * Troca de prazo com preço negociado.
 *
 * O fator financeiro incide sobre o preço que o vendedor acertou (digitado ou
 * mantido), não sobre a tabela: novo = atual × (1 + fNovo) / (1 + fAnterior).
 * Item ainda no preço de tabela é deixado com o valor que o recálculo por
 * tabela já produziu, para não acumular arredondamento a cada troca.
 */

export interface PrecoAntesDoPrazo { item: ProdutoPromocional; valor: number; }

function fatorDoPrazo(prazo: any): number {
  const f = Number(prazo?.fator_financeiro);
  return Number.isFinite(f) ? f : 0;
}

function precoCatalogoSemFator(item: any): number {
  const base = Number(item.price_base != null ? item.price_base : item.price);
  return Number.isFinite(base) && base > 0 ? base : 0;
}

/** Fotografa o unitário dos itens avulsos que acompanham o fator, antes do recálculo do prazo. */
export function capturarPrecosAntesDoPrazo(listas: ProdutoPromocional[][],
  travado: (item: ProdutoPromocional) => boolean): PrecoAntesDoPrazo[] {
  const vistos = new Set<ProdutoPromocional>();
  const precos: PrecoAntesDoPrazo[] = [];
  listas.forEach(lista => (lista || []).forEach(item => {
    if (!item || vistos.has(item)) return;
    vistos.add(item);
    if (item.id_campanha != null || item.bonificacao || item.aplica_fator_financeiro !== true || travado(item)) return;
    const valor = Number(item.valor);
    if (Number.isFinite(valor) && valor > 0) precos.push({ item, valor });
  }));
  return precos;
}

export function prazoReduzFator(prazoAnterior: any, prazoNovo: any): boolean {
  return prazoAnterior != null && fatorDoPrazo(prazoNovo) < fatorDoPrazo(prazoAnterior);
}

/** Itens com quantidade cujo preço cairia com o novo prazo (decide se vale perguntar). */
export function haPrecoParaReduzir(precos: PrecoAntesDoPrazo[]): boolean {
  return precos.some(({ item }) => Number(item.qtde) > 0);
}

/**
 * Reaplica o preço negociado depois do recálculo padrão do prazo.
 * manterPreco: vendedor recusou a redução — o unitário fica como estava e vira a nova base.
 */
export function aplicarPrecoNegociadoNoPrazo(precos: PrecoAntesDoPrazo[], prazoAnterior: any, prazoNovo: any,
  manterPreco: boolean, quatroCasas: (valor: number) => number): void {
  const fatorAnterior = 1 + fatorDoPrazo(prazoAnterior) / 100;
  const fatorNovo = 1 + fatorDoPrazo(prazoNovo) / 100;
  if (fatorAnterior <= 0 || fatorNovo <= 0) return;

  precos.forEach(({ item, valor }) => {
    const tabela = precoCatalogoSemFator(item);
    const noPrecoDeTabela = tabela > 0 && Math.abs(valor - quatroCasas(tabela * fatorAnterior)) < 0.005;
    if (noPrecoDeTabela && !manterPreco) return;

    const novo = quatroCasas(manterPreco ? valor : valor * fatorNovo / fatorAnterior);
    if (!Number.isFinite(novo) || novo <= 0) return;

    const qtde = Number.isFinite(Number(item.qtde)) ? Number(item.qtde) : 0;
    const impostoUnit = Number.isFinite(Number(item.valor_imposto_unitario)) ? Number(item.valor_imposto_unitario) : 0;
    item.valor = novo;
    item.valor_total = parseFloat((novo * qtde).toFixed(2));
    item.valor_imposto_total = parseFloat(((novo + impostoUnit) * qtde).toFixed(2));
    item.valor_imposto_total_final = item.valor_imposto_total;
    item.valor_fator_financeiro_produto = novo;

    /* Desconto exibido relativo à tabela com o novo fator; preço acima da tabela não é desconto */
    if (tabela > 0) {
      const perc = Math.max(0, Math.min(100, (1 - novo / (tabela * fatorNovo)) * 100));
      (item as any).perc_desconto_fixado = perc;
      item.porcentagem_desconto_temporaria = perc > 0 ? quatroCasas(perc) : null;
    }
  });
}
